import React from 'react'

import {
  differenceInSeconds,
  format,
  formatDistance,
  isThisYear,
  isToday,
  max,
} from 'date-fns'

import {
  SAME_TIME_DIFF_IN_SECONDS,
  TIME_DAY_MASK,
  TIME_DAY_YEAR_MASK,
  TIME_MASK,
} from '#util/dates'

import { useThrottledNow } from './timeListeners'

const PRE_PREFIX = 'Last edited '
const PREFIX = PRE_PREFIX + 'at '

const getDelaySeconds = (now: Date, lastUpdated: Date): number => {
  const seconds = differenceInSeconds(now, lastUpdated)
  // update every 10 seconds for the first 90 seconds
  if (seconds < 90) {
    return 10
  }
  // update every 30 seconds for the first 45 minutes
  if (seconds < 45 * 60) {
    return 30
  }
  // update once every half hour after that (who cares at this point?)
  return 60 * 30
}

export const getDateFormatting = ({
  now,
  lastUpdated,
  user,
}: {
  now: Date
  lastUpdated: Date
  user?: string | null
}): string => {
  const byline = user ? ` by ${user} ` : ''
  const seconds = differenceInSeconds(now, lastUpdated)

  // to keep flutter to a minimum we tack the username on last
  if (seconds < SAME_TIME_DIFF_IN_SECONDS) {
    return PRE_PREFIX + 'just now' + byline
  }
  // up to 30 seconds, calculate time ago
  if (seconds < 30) {
    const distance = formatDistance(lastUpdated, now, { addSuffix: true })
    return PRE_PREFIX + distance + byline
  }
  // it's slightly precious, but we dont like what date-fns returns between 30-60 sec
  if (seconds < 60) return PRE_PREFIX + 'less than a minute ago' + byline

  // omit todays date, omit current year
  return isToday(lastUpdated)
    ? PREFIX + format(lastUpdated, TIME_MASK) + byline
    : isThisYear(lastUpdated)
      ? PREFIX + format(lastUpdated, TIME_DAY_MASK) + byline
      : PREFIX + format(lastUpdated, TIME_DAY_YEAR_MASK) + byline
}

const formatter = ({
  now,
  lastUpdated,
  user,
}: {
  now: Date
  lastUpdated: Date
  user?: string | null
}) => {
  // a socket event can carry a timestamp slightly ahead of our local
  // "now" (we haven't ticked yet), which would render as "2 minutes
  // from now"; clamp to the later of the two so we never show a future time
  const nowToUse = max([now, lastUpdated])
  return getDateFormatting({ now: nowToUse, lastUpdated, user })
}

export const useFormattedTimestamp = ({
  date,
  user,
}: {
  date: Date
  user?: string | null
}) => {
  // the delay gets longer the further back in time the doc
  // was last touched
  const [delay, setDelay] = React.useState(1)
  const now = useThrottledNow(delay)
  const [formattedValue, setFormattedValue] = React.useState(
    formatter({ now, lastUpdated: date, user }),
  )

  React.useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setDelay(getDelaySeconds(now, date))
  }, [date, now])

  React.useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setFormattedValue(formatter({ now, lastUpdated: date, user }))
  }, [date, now, user])

  return formattedValue
}
